/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import GameSetup from '../Game/GameSetup';
import { ClientEvent } from '../Game/Integrations/ClientEvent';
import ClientEvents from '../Game/Integrations/ClientEvents';
import IClientEventData from '../Game/Integrations/IClientEventData';
import IClientEventMessage from '../Game/Integrations/IClientEventMessage';
import RecoveryManager from '../Game/RecoveryManager';
import LocalizationComponent from '../Localization/LocalizationComponent';
import BetManager from '../Playbar/BetManager';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import ReferencedComponent from '../Utility/Component/ComponentReference/ReferencedComponent';
import { ReferenceGroup } from '../Utility/Component/ComponentReference/ReferenceGroup';
import { IPopupOptions } from '../Utility/Component/Popup/IPopupModal';
import PopupModal from '../Utility/Component/Popup/PopupModal';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import { isNullOrEmpty, withValue } from '../Utility/Misc';
import { RequestType } from '../Utility/RequestType';
import { AnyFunc } from '../Utility/Types';
import { ErrorPrompt } from './Error/ErrorPrompt';
import IErrorInfo from './Error/IErrorInfo';
import IErrorParams from './Error/IErrorParams';
import IRetries from './Error/IRetries';
import IBalanceUpdateData from './Object/IBalanceUpdateData';
import IHttpRequestData from './Object/IHttpRequestData';
import ILastRequest from './Object/ILastRequest';
import IPlayResultData from './Object/IPlayResultData';
import IPlayResultParameters from './Object/IPlayResultParameters';
import PlayResultComponent from './PlayResultComponent';
import { RGSErrorCode } from './RGSErrorCode';
import { IBetConfig, ISetupResult } from './SetupResult';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Manages all communication between the client and the RGS server.
 */
@cc._decorator.ccclass
export default class Communicator extends StateDependencyComponent
{
    /** How long should the communicator wait (in seconds) before retrying a failed request. */
    @property({ type: cc.Float, visible: true })
    private _retryDelay: number = 3.0;

    /** A reference to the node containing the recall list UI. */
    @property({ type: InjectableComponent, visible: true })
    protected _spinnerNodeRef: InjectableComponent<ReferencedComponent>
        = new InjectableComponent(ReferenceGroup.MainGame, 'Communicator.Spinner');

    /** @hidden */
    private _betManagerRef: InjectableComponent<BetManager> = new InjectableComponent();
    /** @hidden */
    private _clientEventsRef: InjectableComponent<ClientEvents> = new InjectableComponent();
    /** @hidden */
    private _gameSetupRef: InjectableComponent<GameSetup> = new InjectableComponent();
    /** @hidden */
    private _localRef: InjectableComponent<LocalizationComponent> = new InjectableComponent();
    /** @hidden */
    private _playResultRef: InjectableComponent<PlayResultComponent> = new InjectableComponent();
    /** @hidden */
    private _popupModalRef: InjectableComponent<PopupModal> = new InjectableComponent();
    /** @hidden */
    private _recoveryManagerRef: InjectableComponent<RecoveryManager> = new InjectableComponent();

    private static readonly RETRY_INTERVALS: number[] = [ 5, 10, 20, 40, 60 ];

    /** Assembles the default auth data that needs to be included in all requests by default. */
    private get requestPayload(): any
    {
        let version: string[] = this._gameSetupRef.Value.RGSVersion.split('.');

        let adminToken: string = this._gameSetupRef.Value.RGSSession;
        let adminUser: string = this._gameSetupRef.Value.RGSUser;

        let payload: object =
        {
            gameVersion:
            {
                build: version[2],
                major: version[1],
                name: this._gameSetupRef.Value.GameID,
                rgs: version[0]
            },
            jurisdictionId: this._gameSetupRef.Value.Jurisdiction,
            platformId: this._gameSetupRef.Value.PlatformID,
            operatorId: this._gameSetupRef.Value.OperatorID,
            userId: this._gameSetupRef.Value.UserID,
            wagerVersion: this._gameSetupRef.Value.WagerVersion,
        };

        if (adminToken != null)
        {
            payload = Object.assign(payload,
                {
                    rgsUser: adminUser,
                    rgsSession: adminToken
                });
        }
        else
        {
            payload = Object.assign(payload,
                {
                    accessToken: this._gameSetupRef.Value.UserToken
                });
        }

        return payload;
    }

    /** @hidden */
    private _lastRequest: string = "";
    /** @DEBUG Returns the last request issued. */
    public get LastRequest(): string { return this._lastRequest; }
    /** @hidden */
    private _lastResponse: string = "";
    /** @DEBUG Returns the last response string received. */
    public get LastResponse(): string { return this._lastResponse; }
    /** @hidden */
    private _lastReadyStateChange: string = "";
    /** @DEBUG Returns the last http request state change value. */
    public get LastReadyStateChange(): string { return this._lastReadyStateChange; }
    /** @hidden */
    private _lastError: string = "";
    /** @DEBUG Returns the last HTTP error encountered. */
    public get LastError(): string { return this._lastError; }

    private _lastRequestData: ILastRequest = null;

    /** @hidden */
    protected setContexts(): void
    {
        this._betManagerRef.SetContext(BetManager);
        this._clientEventsRef.SetContext(ClientEvents);
        this._gameSetupRef.SetContext(GameSetup);
        this._localRef.SetContext(LocalizationComponent);
        this._playResultRef.SetContext(PlayResultComponent);
        this._popupModalRef.SetContext(PopupModal);
        this._recoveryManagerRef.SetContext(RecoveryManager);
        this._spinnerNodeRef.SetContext(ReferencedComponent);
    }

    protected start(): void
    {
        super.start();

        if (!this._localRef.HasValue())
        {
            CLog.error('Failed to inject localiztion component.', this, LogTypes.Initialization);
        }
    }

    // #region Communicator Functions

    /**
     * All requests to the RGS server are router through this helper function which handles
     * all the boilerplate required of an HTTP request.
     * @param requestData The data for the request being made.
     */
    private makeRequest(
        requestData: IHttpRequestData,
        fnOnComplete?: () => any,
        retries: any = { silent: null, notify: null }): void
    {
        let request: XMLHttpRequest = cc.loader.getXMLHttpRequest();

        // tslint:disable:max-line-length
        let url: string =
            (this._gameSetupRef.Value.RGSServerSettings.RequestType === RequestType.POST)
                ? `${this._gameSetupRef.Value.RGSServer}/${requestData.Command}${this.getParamaterString(requestData.Params)}/`
                : `${this._gameSetupRef.Value.RGSServer}/${requestData.Command}?${this.getParamaterString(requestData.Params)}`;
        // tslint:enable:max-line-length

        request.open(RequestType[this._gameSetupRef.Value.RGSServerSettings.RequestType], url, true);
        request.setRequestHeader("Content-Type", "application/json");
        request.timeout = 15000;

        this._lastRequest = `${requestData.Command} - ${this.getTimestamp()}`;

        let self: Communicator = this;
        request.onreadystatechange = () =>
        {
            self._lastReadyStateChange = `${request.readyState} - ${request.status} - ${self.getTimestamp()}`;

            self._lastRequestData =
            {
                requestData,
                fnOnComplete,
                retries,
            };

            if (request.readyState === 4 && request.status === 200)
            {
                self._spinnerNodeRef.WithValue(spinner => spinner.node.active = false);
                self.onResponse(requestData.Command, request.responseText, requestData.Callback);
            }
            else
            {
                self.errorHandler(request, requestData.Command, retries);
                return;
            }

            if (fnOnComplete) { fnOnComplete(); }
        };

        let payload: any = requestData.Payload ? JSON.stringify(requestData.Payload) : null;

        request.send(payload);
        request.ontimeout = () =>
        {
            self.errorHandler(request, requestData.Command, retries);
        };
    }

    private retryLastRequest(retries: IRetries = null): void
    {
        if (!retries) { retries = { silent: 0, notify: 0, tries: 0 }; }
        else if (!retries.tries && retries.tries !== 0) { retries.tries = 0; }
        else { retries.tries++; }

        this._lastRequestData.retries = retries;

        let retryDelay: number = this._lastRequestData.retries.tries < Communicator.RETRY_INTERVALS.length
            ? Communicator.RETRY_INTERVALS[this._lastRequestData.retries.tries]
            : Communicator.RETRY_INTERVALS[Communicator.RETRY_INTERVALS.length - 1];

        this.scheduleOnce(this.makeRequest
            .bind(this,
                this._lastRequestData.requestData,
                this._lastRequestData.fnOnComplete,
                this._lastRequestData.retries), retryDelay);
    }

    /**
     * Handles error display handling and work flow.
     * @param request The request with error.
     * @returns If retry should be attempted.
     */
    private errorHandler = (request: XMLHttpRequest, command: string, retries: IRetries): number =>
    {
        if (request.readyState !== 4 || (request.status < 400 && request.status !== 0)) { return; }

        const errorInfo: IErrorInfo = request.responseText
            ? JSON.parse(request.responseText)
            : {};

        let handling: IErrorParams;
        if (errorInfo.handleError)
        {
            handling = errorInfo.handleError;
            handling.walletError = errorInfo.walletError ? errorInfo.walletError : null;
            handling.rgsData = errorInfo.rgsData ? errorInfo.rgsData : null;
        }
        else // No error handling data available
        {
            handling = this.getHandling(errorInfo, request.status);
        }

        CLog.error(`Server error:
                        request: ${request.responseURL}
                        status: ${request.status}
                        response: ${request.responseText}`,
                        this,
                        LogTypes.Communicator,
                        true);

        if (errorInfo.errorCode !== RGSErrorCode[RGSErrorCode.WALLET_WAGER_ROLLBACK_COMPLETED]
            && this.tryHandleRetries(handling, retries))
        {
            return;
        }
        else if (errorInfo.errorCode === RGSErrorCode[RGSErrorCode.WALLET_WAGER_ROLLBACK_COMPLETED])
        {
            this._clientEventsRef.Value.ReportError(errorInfo.errorCode, handling);
            return;
        }

        this._clientEventsRef.Value.ReportError(errorInfo.errorCode, handling);
        this._spinnerNodeRef.Value.node.active = false;

        // tslint:disable-next-line:max-line-length
        CLog.error(`Server call resulted in an invalid response: (request: ${request.responseURL} -  status: ${request.status}): ${request.responseText}`, 
            this, LogTypes.Communicator, true);
    }

    private tryHandleRetries(handling: IErrorParams, retries: IRetries): boolean
    {
        if (handling.silentRetry
            && handling.silentRetry > 0
            && (retries.silent == null || retries.silent > 0))
        {
            this.silentRetry(handling, retries);
            return true;
        }

        if (handling.notifyRetry
            && handling.notifyRetry > 0
            && (retries.notify == null || retries.notify > 0))
        {
            this.notifyRetry(handling, retries);
            return true;
        }

        if (!handling.prompts || handling.prompts.length < 1) { return true; }

        handling.handlePrompt = this.handlePrompt;
        return false;
    }

    private silentRetry(handling: IErrorParams, retries: IRetries): void
    {
        if (retries.silent === null)
        {
            retries.silent = handling.silentRetry;
        }
        retries.silent--;

        CLog.log(`SILENT RETRY - [${retries.silent}] REMAINING`, LogTypes.Communicator, this);
        this.retryLastRequest(retries);
    }

    private notifyRetry(handling: IErrorParams, retries: IRetries): void
    {
        this.showRetryNotification();

        if (retries.notify === null)
        {
            retries.notify = handling.notifyRetry;
        }
        retries.notify--;

        CLog.log(`NOTIFY RETRY - [${retries.notify}] REMAINING`, LogTypes.Communicator, this);
        this.retryLastRequest(retries);
    }

    private getHandling(errorInfo: IErrorInfo, statusCode: number): IErrorParams
    {
        let notifyRetry: number = 2;
        let prompts: ErrorPrompt[] = [ ErrorPrompt.Reload ];

        // Zuul Exception settings
        if (errorInfo.exception && errorInfo.exception.indexOf('ZuulException') > -1)
        {
            notifyRetry = 1000;
            prompts = null;
        }

        const errorCode: number = statusCode === 0 && navigator.onLine === false ? 5 : 1;
        errorInfo.errorCode = errorInfo.errorCode || errorInfo.error;

        let errorParams: IErrorParams = {
            silentRetry: 1,
            notifyRetry,
            textCode: errorCode.toString(),
            traceId: '',
            prompts
        };

        if (errorInfo.rgsData) { errorParams.rgsData = errorInfo.rgsData; }

        return errorParams;
    }

    private showRetryNotification(): void
    {
        this._spinnerNodeRef.WithValue(spinner => spinner.node.active = true);
    }

    private handlePrompt = (prompt: string, options: IPopupOptions, buttonIndex: number): number =>
    {
        let action: AnyFunc = null;

        switch (prompt)
        {
            case ErrorPrompt.ExitToLobby:
                action = () => this._gameSetupRef.Value.NavigateToLobby();
                break;
            case ErrorPrompt.Reload:
                action = () => this._gameSetupRef.Value.ReloadPage();
                break;
            case ErrorPrompt.Retry:
                action = () => this.retryLastRequest();
                break;
            case ErrorPrompt.Close:
                buttonIndex = 1;
                options.IncludeOkButton = false;
            default:
                break;
        }

        if (buttonIndex === 0)
        {
            options.IncludeOkButton = true;
            options.OkButtonText = this._localRef.Value.GetStringResource("Errors/" + prompt);
            options.FnOnOK = action;
        }
        else if (buttonIndex === 1)
        {
            options.IncludeCancelButton = true;
            options.CancelButtonText = this._localRef.Value.GetStringResource("Errors/" + prompt);
            options.FnOnCancel = action;
        }

        return 0;
    }

    public get HandlePrompt(): AnyFunc { return this.handlePrompt; }

    /**
     * Generic entry point for every request response.
     * @param command The command executed by the request to the RGS server.
     * @param payload The response payload returned from the request.
     * @param callback (Optional) The specified handler for the response payload.
     */
    private onResponse = (command: string, payload: string, callback: (result: string) => void): void =>
    {
        CLog.log(`Payload: ${payload}`, LogTypes.Communicator, this);

        this._lastResponse = `${command} - ${this.getTimestamp()}`;
        if (callback) { callback(payload); }
    }

    /**
     * Helper for assembling a parameter string to be added to a URL.
     * @param params The parameters to be concatenated.
     * @returns The concatenated params.
     * @hidden
     */
    private getParamaterString = (params: any): string =>
    {
        let url: string = "";

        switch (this._gameSetupRef.Value.RGSServerSettings.RequestType)
        {
            case RequestType.GET:
                Object.keys(params).forEach((key) => url += `${key}=${params[key]}&`);
                break;
            case RequestType.POST:
                Object.keys(params).forEach((key) => url += `/${params[key]}`);
                break;
        }

        return url;
    }

    /** @hidden */
    private getTimestamp(): string
    {
        return (Date.now() % 1000000).toString();
    }

    // #endregion

    // #region Service Requests
    // tslint:disable:object-literal-sort-keys

    /**
     * @DEBUG Requests a specific play result from the RGS server for testing purposes.
     * Not valid in production.
     * @param prizeIndex The prizeIndex to force.
     * @param prizeBranchIndex The branch of the prizeIndex to force.
     * @param stopDataIndex Specified stop data for the forced prizeIndex.
     * @param fnCallback The callback to process the returned play result.
     */
    public ForcePlayResult = (
        prizeIndex: number,
        prizeBranchIndex: number,
        stopDataIndex: number,
        bonusScriptListIds: number[],
        fnCallback: (result: string) => void): void =>
    {
        const betManager: BetManager = this._betManagerRef.Value;
        if (!betManager) { return; }
        const betConfig: IBetConfig = betManager.CurrentBetConfig;

        let params: IPlayResultParameters =
        {
            denom: betManager.ActiveDenom,
            paytable: betManager.Paytable.toString(),
            payoutID: betManager.PayoutID,
            betConfigID: (betConfig) ? betConfig.betID : 0
        };

        let betScale: number = -1;
        if (!isNullOrEmpty(this._gameSetupRef.WithValue(g => g.BetScalers)))
        {
            betScale = betManager.CurrentBetScaler;
        }

        if (bonusScriptListIds.length === 0) { bonusScriptListIds = null; }

        let currentRoundId: number = null;
        let currentPlayId: number = null;

        this._playResultRef.WithValue(playResult =>
        {
            withValue(playResult.ResultData, data =>
            {
                currentRoundId = data.PlayResult.PlaySequenceNumber;
                currentPlayId = data.PlayResult.playId;
            });
        });

        this._gameSetupRef.WithValue(gameSetup =>
        {
            if (currentRoundId == null) { currentRoundId = gameSetup.CurrentRound; }
            if (currentPlayId == null) { currentPlayId = gameSetup.CurrentPlay; }
        });

        const requestData: IHttpRequestData =
        {
            Command: 'requestPrizeForce',
            Params: params,
            Payload: Object.assign(this.requestPayload,
            {
                prizeForceOption:
                {
                    prizeBranchIndex,
                    prizeIndex,
                    stopDataIndex,
                    bonusScriptListIds
                },
                lastRoundId: currentRoundId,
                lastPlayId: currentPlayId,
                platformBetMult: betScale
            }),
            Callback: (result: string) => fnCallback(result)
        };

        this.makeRequest(requestData);
    }

    /**
     * Requests a play result from the RGS server given the player's current wager.
     * @param fnCallback The function to process the play result returned from the RGS server.
     */
    public RequestPlayResult = (
        fnCallback: (result: string) => void,
        roundId: number = -1,
        playId: number = -1): void =>
    {
        const betManager: BetManager = this._betManagerRef.Value;
        const betConfig: IBetConfig = betManager.CurrentBetConfig;

        const params: IPlayResultParameters =
        {
            denom: betManager.ActiveDenom,
            paytable: betManager.Paytable.toString(),
            payoutID: betManager.PayoutID,
            betConfigID: betConfig ? betConfig.betID : 0
        };

        const gameSetup: GameSetup = this._gameSetupRef.Value;

        let betScale: number = -1;
        if (gameSetup.BetScalers && gameSetup.BetScalers.length > 0)
        {
            betScale = betManager.CurrentBetScaler;
        }

        const resultData: IPlayResultData = this._playResultRef.WithValue(result => result.ResultData);

        let currentRoundId: number = null;
        let currentPlayId: number = null;
        if (roundId >= 0 && playId >= 0)
        {
            currentRoundId = roundId;
            currentPlayId = playId;
        }
        else if (resultData)
        {
            currentRoundId = resultData.PlayResult.PlaySequenceNumber;
            currentPlayId = resultData.PlayResult.playId;
        }
        else
        {
            currentRoundId = gameSetup.CurrentRound;
            currentPlayId = gameSetup.CurrentPlay;
        }

        const requestData: IHttpRequestData =
        {
            Command: 'requestPlay',
            Params: params,
            Payload: Object.assign(this.requestPayload,
            {
                lastRoundId: currentRoundId,
                lastPlayId: currentPlayId,
                platformBetMult: betScale
            }),

            Callback: result => fnCallback(result)
        };

        this.makeRequest(requestData);
    }

    /**
     * Requests a play result from the player's history for recall purposes.
     * @param roundID The round ID of the requested play result.
     * @param playID The play ID of the requested play result.
     * @param fnCallback The function to process the returned play result.
     */
    public RequestPreviousResult = (roundID: number, playID: number, fnCallback: (result: string) => any): void =>
    {
        roundID = roundID || 0;
        let requestData: IHttpRequestData =
        {
            Command: "getRecallPlay",
            Params:
            {
                roundID,
                playID
            },
            Payload: Object.assign(this.requestPayload,
            {
                platformRoundId: this._gameSetupRef.Value.PlatformRecallRoundID
            }),
            Callback: (result: string) => fnCallback(result)
        };

        this.makeRequest(requestData);
    }

    /**
     * Fetches a list of recent plays for display in the recall summary screen.
     * @param roundID The starting roundID to begin fetching from.
     * @param numRounds The number of rounds to go back from the starting roundID.
     * @param fnCallback The function to process the recall summary response returned from the RGS server.
     */
    public RequestRecallSummary(roundID: number, numRounds: number, fnCallback: (result: string) => void): void
    {
        if (this._gameSetupRef.Value.Recall || this._gameSetupRef.Value.PlatformRecallRoundID != null)
        {
            return;
        }

        let requestData: IHttpRequestData =
        {
            Command: "getRecallSummary",
            Params:
            {
                totalRounds: numRounds,
                roundID
            },
            Payload: this.requestPayload,
            Callback: (result: string) => fnCallback(result)
        };

        this.makeRequest(requestData);
    }

    /**
     * Fetches the configured reel strips to use for this game.
     * @param reelSetID The reel set to be fetched.
     * @param symbolSetID The symbol set being used for these reels.
     * @param fnCallback The function to process the returned reel strip data.
     */
    public RequestReelStrips = (reelSetID: Number,
                                symbolSetID: Number,
                                fnCallback: (reels: any) => void): void =>
    {
        let requestData: IHttpRequestData =
        {
            Command: "requestReelStrips",
            Params:
            {
                reelSetID
            },
            Payload: this.requestPayload,
            Callback: (result: string) =>
            {
                try
                {
                    result = JSON.parse(result);
                }
                catch (error)
                {
                    CLog.error(error, this, LogTypes.Communicator);
                    return;
                }

                fnCallback(result);
            }
        };

        if (this._gameSetupRef.Value.RGSServerSettings.RequestType === RequestType.GET)
        {
            requestData.Params.symbolSetID = symbolSetID;
        }

        this.makeRequest(requestData);
    }

    /**
     * Requests setup and configuration data for this game from the RGS server.
     * @param fnCallback The function process the setup data returned from the RGS.
     */
    public RequestSetup = (fnCallback: (result: ISetupResult) => void): void =>
    {
        let requestData: IHttpRequestData =
        {
            Command: 'requestSetup',
            Params: {},
            Payload: this.requestPayload,
            Callback: (result: string) => fnCallback(result ? JSON.parse(result) : null)
        };

        const cacheProperty: string = 'cachedSetupReq';
        const cachedRequest: XMLHttpRequest = window[cacheProperty];

        if (!CC_JSB && cachedRequest)
        {
            window[cacheProperty] = null;

            this._lastReadyStateChange
                = `${cachedRequest.readyState} - ${cachedRequest.status} - ${this.getTimestamp()}`;

            this._lastRequestData =
            {
                requestData,
                fnOnComplete: null,
                retries: { silent: null, notify: null }
            };

            if (cachedRequest.readyState === 4 && cachedRequest.status === 200)
            {
                this._spinnerNodeRef.Value.node.active = false;
                this.onResponse(requestData.Command, cachedRequest.responseText, requestData.Callback.bind(this));
            }
            else
            {
                this.errorHandler(cachedRequest, requestData.Command, { silent: null, notify: null });
            }
        }
        else
        {
            this.makeRequest(requestData);
        }
    }

    /**
     * Requests setup and configuration data for this game from the RGS server.
     * @param fnCallback The function process the setup data returned from the RGS.
     */
    public RequestRecallSetup = (fnCallback: (result: ISetupResult) => void): void =>
    {
        let recallRound: number = this._gameSetupRef.Value.PlayID;
        let requestData: IHttpRequestData =
        {
            Command: "requestRecallSetup",
            Params: {},
            Payload: Object.assign(this.requestPayload,
            {
                roundId: recallRound,
                platformRoundId: this._gameSetupRef.Value.PlatformRecallRoundID
            }),
            Callback: (result: string) => fnCallback(result ? JSON.parse(result) : null)
        };

        this.makeRequest(requestData);
    }

    /**
     * Request balance to update users balance from server.
     */
    public RequestBalance = (fnCallback: (result: IBalanceUpdateData) => void): void =>
    {
        let requestData: IHttpRequestData =
        {
            Command: "requestBalance",
            Params: {},
            Payload: this.requestPayload,
            Callback: (result: string) => fnCallback(result ? JSON.parse(result) : null)
        };

        this.makeRequest(requestData);
    }

    public ReportClientEvent = (event: ClientEvent, data: IClientEventData = {}): void =>
    {
        data.gameId = this._gameSetupRef.Value.GameID;
        if (!data.userId) { data.userId = this._gameSetupRef.Value.UserID; }

        const clientEvent: IClientEventMessage =
        {
            id: ClientEvent[event],
            data,
            isRecovered: this._recoveryManagerRef.WithValue(mgr => mgr.Recovering, false)
        };

        if (CC_JSB)
        {
            // TODO : native handling of client events, if any
        }
        else
        {
            window.parent.postMessage(JSON.stringify(clientEvent), "*");
        }
    }

    // #endregion
}
